import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLang } from '../../contexts/LangContext';
import { useAuth } from '../../contexts/AuthContext';
import { buildingUtilityApi } from '../../api/buildingUtilityApi';
import { buildingsApi, Building } from '../../api/buildingsApi';
import { BuildingUtility, BuildingUtilityGroup, PricingTier } from '../../types';
import PricingTierModal from '../../components/modals/PricingTierModal';
import { AdvanceBookingDaysFields } from '../../components/building-utilities/AdvanceBookingDaysFields';

export const AddEditUtility: React.FC = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const buildingIdFromQuery = searchParams.get('buildingId');
  
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [utilityGroups, setUtilityGroups] = useState<BuildingUtilityGroup[]>([]);
  const [selectedUtilityType, setSelectedUtilityType] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [editingServiceHourIndex, setEditingServiceHourIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: '',
    isActive: true,
    location: '',
    amenities: [] as string[],
    buildingId: buildingIdFromQuery || '',
    groupId: '',
    startDate: '',
    endDate: '',
    serviceHours: [] as any[],
    // Booking settings
    advanceBookingDays: { min: 1, max: 7 },
    staffAdvanceBookingDays: { min: 0, max: 60 },
    usageLimitPerWeek: '',
    usageLimitPerMonth: '',
    maxUsageTimePerWeek: '',
    maxUsageTimePerMonth: '',
    maxSlotsPerBooking: '',
    // Pricing tiers are now part of serviceHours
    // Additional fees
    additionalFees: [] as any[],
    // Cancellation policy
    cancellationPolicies: [] as any[],
    // Time slots for FIXED_SLOTS
    timeSlots: [] as any[],
    pricing: {},
    availableSlots: {},
    depositAmount: '' // Tiền đặt cọc
  });

  const isEdit = !!id;

  useEffect(() => {
    loadBuildings();
    if (isEdit) {
      loadUtility();
    }
  }, [id]);

  useEffect(() => {
    if (formData.buildingId && !isEdit) {
      loadUtilityGroups(formData.buildingId);
    }
  }, [formData.buildingId, isEdit]);

  useEffect(() => {
    if (formData.groupId) {
      const selectedGroup = utilityGroups.find(g => g.id === formData.groupId);
      if (selectedGroup) {
        setSelectedUtilityType(selectedGroup.utilityType);
      }
    }
  }, [formData.groupId, utilityGroups]);

  const loadBuildings = async () => {
    try {
      const response = await buildingsApi.getBuildings();
      if (response && Array.isArray(response)) {
        setBuildings(response);
        // Auto-select user's building if available and no building specified
        if (!buildingIdFromQuery && user?.buildingId) {
          setFormData(prev => ({ ...prev, buildingId: user.buildingId! }));
        }
      }
    } catch (err: any) {
      setError('Error loading buildings');
    }
  };

  const loadUtilityGroups = async (buildingId?: string) => {
    const targetBuildingId = buildingId || formData.buildingId;
    if (!targetBuildingId) return;
    
    try {
      const response = await buildingUtilityApi.getUtilityGroups(targetBuildingId, {
        page: 1,
        limit: 200,
      });
      if (response && Array.isArray(response.data)) {
        setUtilityGroups(response.data);
      }
    } catch (err: any) {
      console.error('Error loading utility groups:', err);
    }
  };

  const loadUtility = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const utility = await buildingUtilityApi.getUtility(id);
      const groupsResponse = await buildingUtilityApi.getUtilityGroups(utility.buildingId, {
        page: 1,
        limit: 200,
      });
      const loadedGroups = Array.isArray(groupsResponse.data) ? groupsResponse.data : [];
      let mergedGroups = loadedGroups;
      if (utility.groupId && !loadedGroups.some((group) => group.id === utility.groupId)) {
        try {
          const group = await buildingUtilityApi.getUtilityGroup(utility.groupId);
          mergedGroups = [...loadedGroups, group];
        } catch {
          mergedGroups = loadedGroups;
        }
      }
      setUtilityGroups(mergedGroups);
      if (utility.group?.utilityType) {
        setSelectedUtilityType(utility.group.utilityType);
      }
      setFormData({
        name: utility.name,
        description: utility.description || '',
        image: utility.image || '',
        isActive: utility.isActive,
        location: utility.location || '',
        amenities: utility.amenities || [],
        buildingId: utility.buildingId,
        groupId: utility.groupId,
        startDate: utility.startDate ? (utility.startDate instanceof Date ? utility.startDate.toISOString().split('T')[0] : utility.startDate) : '',
        endDate: utility.endDate ? (utility.endDate instanceof Date ? utility.endDate.toISOString().split('T')[0] : utility.endDate) : '',
        serviceHours: utility.serviceHours || [],
        advanceBookingDays: {
          min: utility.advanceBookingDays?.min ?? 1,
          max: utility.advanceBookingDays?.max ?? 7,
        },
        staffAdvanceBookingDays: {
          min: utility.staffAdvanceBookingDays?.min ?? 0,
          max: utility.staffAdvanceBookingDays?.max ?? 60,
        },
        usageLimitPerWeek: utility.usageLimitPerWeek?.toString() || '',
        usageLimitPerMonth: utility.usageLimitPerMonth?.toString() || '',
        maxUsageTimePerWeek: utility.maxUsageTimePerWeek?.toString() || '',
        maxUsageTimePerMonth: utility.maxUsageTimePerMonth?.toString() || '',
        maxSlotsPerBooking: utility.maxSlotsPerBooking?.toString() || '',
        additionalFees: utility.additionalFees || [],
        cancellationPolicies: utility.cancellationPolicies || [],
        timeSlots: utility.timeSlots || [],
        pricing: utility.pricing || {},
        availableSlots: utility.availableSlots || {},
        depositAmount: utility.depositAmount?.toString() || ''
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error loading utility');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.buildingId) {
      setError('Vui lòng chọn tòa nhà');
      return;
    }

    if (!formData.groupId) {
      setError('Vui lòng chọn nhóm tiện ích');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const depositAmount = parseDepositAmountForSubmit(formData.depositAmount);

      const submitData = {
        name: formData.name,
        description: formData.description || undefined,
        image: formData.image || undefined,
        isActive: formData.isActive,
        location: formData.location || undefined,
        amenities: formData.amenities,
        buildingId: formData.buildingId,
        groupId: formData.groupId,
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
        serviceHours: sanitizeServiceHoursForSubmit(formData.serviceHours),
        advanceBookingDays: formData.advanceBookingDays,
        staffAdvanceBookingDays: formData.staffAdvanceBookingDays,
        usageLimitPerWeek: formData.usageLimitPerWeek ? parseInt(formData.usageLimitPerWeek, 10) : undefined,
        usageLimitPerMonth: formData.usageLimitPerMonth ? parseInt(formData.usageLimitPerMonth, 10) : undefined,
        maxUsageTimePerWeek: formData.maxUsageTimePerWeek ? parseInt(formData.maxUsageTimePerWeek, 10) : undefined,
        maxUsageTimePerMonth: formData.maxUsageTimePerMonth ? parseInt(formData.maxUsageTimePerMonth, 10) : undefined,
        maxSlotsPerBooking: formData.maxSlotsPerBooking ? parseInt(formData.maxSlotsPerBooking, 10) : undefined,
        additionalFees: formData.additionalFees,
        cancellationPolicies: formData.cancellationPolicies,
        timeSlots: formData.timeSlots,
        pricing: formData.pricing,
        availableSlots: formData.availableSlots,
        depositAmount,
      };

      if (isEdit) {
        await buildingUtilityApi.updateUtility(id!, submitData);
      } else {
        await buildingUtilityApi.createUtility(submitData);
      }

      navigate('/building-utilities');
    } catch (err: any) {
      setError(err.response?.data?.message || `Error ${isEdit ? 'updating' : 'creating'} utility`);
    } finally {
      setLoading(false);
    }
  };

  const formatNumberDisplay = (value: number | string) => {
    if (!value && value !== 0) return '';
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/\./g, '')) : value;
    if (isNaN(numValue)) return '';
    return new Intl.NumberFormat('vi-VN').format(numValue);
  };

  const parseNumberInput = (formattedValue: string) => formattedValue.replace(/\./g, '');

  const parseDepositAmountForSubmit = (raw: string): number | null => {
    const digits = parseNumberInput(raw).replace(/[^\d]/g, '');
    if (!digits) return null;
    const n = Number(digits);
    return Number.isFinite(n) ? n : null;
  };

  const sanitizeServiceHoursForSubmit = (hours: any[]) =>
    hours.map((hour) => ({
      days: Array.isArray(hour.days) ? hour.days : [],
      startTime: hour.startTime,
      endTime: hour.endTime,
      ...(hour.maxCapacity !== undefined && hour.maxCapacity !== null && hour.maxCapacity !== ''
        ? { maxCapacity: Number(hour.maxCapacity) }
        : {}),
      pricingTiers: Array.isArray(hour.pricingTiers)
        ? hour.pricingTiers.map((tier: PricingTier) => ({
            slotThreshold: Number(tier.slotThreshold),
            pricePerSlot: Number(tier.pricePerSlot),
            ...(tier.description ? { description: tier.description } : {}),
          }))
        : [],
    }));

  const handleDepositAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = parseNumberInput(e.target.value).replace(/[^\d]/g, '');
    setFormData((prev) => ({ ...prev, depositAmount: raw }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleAmenitiesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const amenities = e.target.value.split('\n').filter(item => item.trim() !== '');
    setFormData(prev => ({ ...prev, amenities }));
  };

  // Service Hours Management
  const addServiceHour = () => {
    setFormData(prev => ({
      ...prev,
      serviceHours: [
        ...prev.serviceHours,
        {
          days: [],
          startTime: '',
          endTime: '',
          maxCapacity: selectedUtilityType === 'NOTIFICATION_ONLY' ? 1 : 0,
          pricingTiers: [],
        },
      ],
    }));
  };

  const updateServiceHour = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      serviceHours: prev.serviceHours.map((hour, i) => 
        i === index ? { ...hour, [field]: value } : hour
      )
    }));
  };

  const removeServiceHour = (index: number) => {
    setFormData(prev => ({
      ...prev,
      serviceHours: prev.serviceHours.filter((_, i) => i !== index)
    }));
  };

  // Pricing Tiers Management for Service Hours
  const handleServiceHourPricingTiersSave = (serviceHourIndex: number, pricingTiers: PricingTier[]) => {
    setFormData(prev => ({
      ...prev,
      serviceHours: prev.serviceHours.map((hour, index) => 
        index === serviceHourIndex 
          ? { ...hour, pricingTiers }
          : hour
      )
    }));
  };

  // Additional Fees Management
  const addAdditionalFee = () => {
    setFormData(prev => ({
      ...prev,
      additionalFees: [...prev.additionalFees, { name: '', amount: '', collectWhen: 'BOOKING' }]
    }));
  };

  const updateAdditionalFee = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      additionalFees: prev.additionalFees.map((fee, i) => 
        i === index ? { ...fee, [field]: value } : fee
      )
    }));
  };

  const removeAdditionalFee = (index: number) => {
    setFormData(prev => ({
      ...prev,
      additionalFees: prev.additionalFees.filter((_, i) => i !== index)
    }));
  };

  // Cancellation Policy Management
  const addCancellationPolicy = () => {
    setFormData(prev => ({
      ...prev,
      cancellationPolicies: [...prev.cancellationPolicies, { 
        hoursBeforeStart: '', 
        refundType: 'PERCENTAGE', 
        refundValue: '' 
      }]
    }));
  };

  const updateCancellationPolicy = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      cancellationPolicies: prev.cancellationPolicies.map((policy, i) => 
        i === index ? { ...policy, [field]: value } : policy
      )
    }));
  };

  const removeCancellationPolicy = (index: number) => {
    setFormData(prev => ({
      ...prev,
      cancellationPolicies: prev.cancellationPolicies.filter((_, i) => i !== index)
    }));
  };

  // Time Slots Management (for FIXED_SLOTS)
  const addTimeSlot = () => {
    setFormData(prev => ({
      ...prev,
      timeSlots: [...prev.timeSlots, { startTime: '', endTime: '', capacity: '' }]
    }));
  };

  const updateTimeSlot = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      timeSlots: prev.timeSlots.map((slot, i) => 
        i === index ? { ...slot, [field]: value } : slot
      )
    }));
  };

  const removeTimeSlot = (index: number) => {
    setFormData(prev => ({
      ...prev,
      timeSlots: prev.timeSlots.filter((_, i) => i !== index)
    }));
  };

  if (loading && isEdit) {
    return (
      <div className="max-w-4xl mx-auto py-8">
        <div className="text-center">
          <div className="text-lg text-gray-600">Đang tải thông tin tiện ích...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8">
      {/* Header */}
      <div className="mb-8">
        <nav className="text-sm text-gray-500 mb-4">
          <span>Tiện ích tòa nhà</span> / 
          <span className="text-gray-900 ml-1">
            {isEdit ? 'Chỉnh sửa tiện ích' : 'Tạo tiện ích'}
          </span>
        </nav>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {isEdit ? 'Chỉnh sửa tiện ích' : 'Tạo tiện ích mới'}
        </h1>
        <p className="text-gray-600">
          {isEdit ? 'Cập nhật thông tin tiện ích' : 'Tạo tiện ích mới cho tòa nhà'}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Thông tin cơ bản</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Building Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tòa nhà <span className="text-red-500">*</span>
                </label>
                <select
                  name="buildingId"
                  value={formData.buildingId}
                  onChange={handleInputChange}
                  disabled={isEdit}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  required
                >
                  <option value="">-- Chọn tòa nhà --</option>
                  {buildings.map((building) => (
                    <option key={building.id} value={building.id}>
                      {building.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Utility Group Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nhóm tiện ích <span className="text-red-500">*</span>
                </label>
                <select
                  name="groupId"
                  value={formData.groupId}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">-- Chọn nhóm tiện ích --</option>
                  {utilityGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.icon} {group.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tên tiện ích <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ví dụ: Hồ bơi tầng trệt"
                  required
                />
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vị trí
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ví dụ: Tầng trệt, khu A"
                />
              </div>

              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ngày bắt đầu
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ngày kết thúc (tùy chọn)
                </label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mô tả
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Mô tả về tiện ích này..."
                />
              </div>

              {/* Status */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">Tiện ích đang hoạt động</span>
                </label>
              </div>
            </div>
          </div>

          {selectedUtilityType === 'NOTIFICATION_ONLY' && (
            <div className="bg-gray-50 p-4 rounded-lg space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Đặt cọc</h3>
                <div className="max-w-md">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tiền đặt cọc (VND)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="depositAmount"
                    value={formatNumberDisplay(formData.depositAmount)}
                    onChange={handleDepositAmountChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Nhập số tiền đặt cọc..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Số tiền cư dân cần đặt cọc khi đăng ký sử dụng. Ban quản lý thu cọc sau khi xác nhận đăng ký.
                  </p>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Cài đặt đặt trước (cư dân)</h3>
                <AdvanceBookingDaysFields
                  className="max-w-2xl"
                  value={formData.advanceBookingDays}
                  onChange={(advanceBookingDays) =>
                    setFormData((prev) => ({ ...prev, advanceBookingDays }))
                  }
                />
                <p className="mt-2 text-xs text-gray-500 max-w-2xl">
                  Ví dụ tiệc cưới / phòng sinh hoạt: 60–90 ngày; đăng ký ngắn hạn: 7 ngày.
                </p>
                <h4 className="text-sm font-semibold text-gray-800 mt-6 mb-3">Cho nhân viên (đăng ký tại quầy)</h4>
                <AdvanceBookingDaysFields
                  className="max-w-2xl"
                  allowNegativeMin
                  value={formData.staffAdvanceBookingDays}
                  onChange={(staffAdvanceBookingDays) =>
                    setFormData((prev) => ({ ...prev, staffAdvanceBookingDays }))
                  }
                  minLabel="Đặt trước tối thiểu (ngày) cho nhân viên"
                  maxLabel="Đặt trước tối đa (ngày) cho nhân viên"
                />
              </div>
            </div>
          )}

          {/* Service Hours */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Thời gian phục vụ</h3>
              <button
                type="button"
                onClick={addServiceHour}
                className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm"
              >
                + Thêm khung giờ
              </button>
            </div>
            {formData.serviceHours.length === 0 && (
              <p className="text-gray-500 text-sm mb-4">Chưa có khung giờ phục vụ nào. Nhấn "Thêm khung giờ" để bắt đầu.</p>
            )}
            {formData.serviceHours.map((hour, index) => (
              <div key={index} className="border border-gray-200 p-4 rounded-md mb-4 bg-white">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Các ngày trong tuần
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {['Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7', 'Chủ nhật'].map((day, dayIndex) => (
                        <label key={dayIndex} className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={hour.days.includes(dayIndex + 1)}
                            onChange={(e) => {
                              const newDays = e.target.checked 
                                ? [...hour.days, dayIndex + 1]
                                : hour.days.filter((d: number) => d !== dayIndex + 1);
                              updateServiceHour(index, 'days', newDays);
                            }}
                            className="mr-1"
                          />
                          {day}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Giờ bắt đầu
                    </label>
                    <input
                      type="time"
                      value={hour.startTime}
                      onChange={(e) => updateServiceHour(index, 'startTime', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Giờ kết thúc
                    </label>
                    <input
                      type="time"
                      value={hour.endTime}
                      onChange={(e) => updateServiceHour(index, 'endTime', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Max Capacity for FIXED_SLOTS, NO_BOOKING, and NOTIFICATION_ONLY */}
                {(selectedUtilityType === 'FIXED_SLOTS' ||
                  selectedUtilityType === 'NO_BOOKING' ||
                  selectedUtilityType === 'NOTIFICATION_ONLY') && (
                  <div className="bg-yellow-50 p-3 rounded-lg mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {selectedUtilityType === 'NOTIFICATION_ONLY'
                        ? 'Số lượt đăng ký tối đa cho khung giờ này'
                        : 'Số lượng (người/lượt) tối đa cho khung giờ này'}
                    </label>
                    <input
                      type="number"
                      value={
                        selectedUtilityType === 'NOTIFICATION_ONLY'
                          ? (hour as any).maxCapacity ?? 1
                          : (hour as any).maxCapacity || ''
                      }
                      onChange={(e) => updateServiceHour(index, 'maxCapacity', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min={selectedUtilityType === 'NOTIFICATION_ONLY' ? 1 : 0}
                      placeholder={
                        selectedUtilityType === 'NOTIFICATION_ONLY'
                          ? 'Mặc định 1 (một tiệc/sự kiện mỗi khung)'
                          : 'Nhập số lượng tối đa (0 = không giới hạn)...'
                      }
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {selectedUtilityType === 'NOTIFICATION_ONLY'
                        ? 'Mặc định 1 lượt/khung (ví dụ một đám cưới). Tăng nếu cho phép nhiều đăng ký cùng khung.'
                        : `Số người/lượt tối đa có thể ${selectedUtilityType === 'FIXED_SLOTS' ? 'đặt cho khung giờ này' : 'sử dụng cùng lúc'}. Nhập 0 để không giới hạn sức chứa.`}
                    </p>
                  </div>
                )}

                {/* Pricing Tiers for this Service Hour */}
                {(selectedUtilityType === 'TIME_BASED' || selectedUtilityType === 'FIXED_SLOTS' || selectedUtilityType === 'NO_BOOKING') && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-sm font-semibold text-gray-800">
                        Định mức giá cho khung giờ này ({selectedUtilityType === 'FIXED_SLOTS' ? 'theo số người/lượt' : selectedUtilityType === 'NO_BOOKING' ? 'theo số người' : 'theo số giờ'})
                      </h4>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingServiceHourIndex(index);
                          setIsPricingModalOpen(true);
                        }}
                        className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-xs"
                      >
                        {(hour as any).pricingTiers?.length > 0 ? 'Chỉnh sửa giá' : '+ Thiết lập giá'}
                      </button>
                    </div>
                    
                    {!(hour as any).pricingTiers?.length ? (
                      <div className="text-center py-4 border border-blue-200 rounded bg-white">
                        <div className="text-blue-400 mb-1">
                          <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                        </div>
                        <p className="text-blue-600 text-xs">Chưa thiết lập định mức giá</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {((hour as any).pricingTiers || []).map((tier: PricingTier, tierIndex: number) => (
                          <div key={tierIndex} className="flex justify-between items-center bg-white p-2 rounded border">
                            <div>
                              <span className="text-sm font-medium text-gray-700">
                                Từ {tier.slotThreshold} {selectedUtilityType === 'FIXED_SLOTS' ? 'người/lượt' : selectedUtilityType === 'NO_BOOKING' ? 'người' : 'giờ'}
                              </span>
                              {tier.description && (
                                <span className="text-xs text-gray-500 ml-2">({tier.description})</span>
                              )}
                            </div>
                            <div className="text-blue-600 font-semibold text-sm">
                              {new Intl.NumberFormat('vi-VN', {
                                style: 'currency',
                                currency: 'VND'
                              }).format(tier.pricePerSlot)}/{selectedUtilityType === 'FIXED_SLOTS' ? 'người/lượt' : selectedUtilityType === 'NO_BOOKING' ? 'người' : 'giờ'}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-end mt-3">
                  <button
                    type="button"
                    onClick={() => removeServiceHour(index)}
                    className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm"
                  >
                    Xóa khung giờ
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Booking Settings for TIME_BASED, FIXED_SLOTS, and NO_BOOKING */}
          {(selectedUtilityType === 'TIME_BASED' || selectedUtilityType === 'FIXED_SLOTS' || selectedUtilityType === 'NO_BOOKING') && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Cài đặt đặt trước (cư dân)</h3>
              <AdvanceBookingDaysFields
                value={formData.advanceBookingDays}
                onChange={(advanceBookingDays) =>
                  setFormData((prev) => ({ ...prev, advanceBookingDays }))
                }
              />
              <p className="mt-2 text-xs text-gray-500">
                Ví dụ: phòng sinh hoạt / tiệc cưới thường cần 60–90 ngày; BBQ ngắn hạn có thể giữ 7 ngày.
              </p>
              <h4 className="text-sm font-semibold text-gray-800 mt-6 mb-3">Cho nhân viên (đăng ký tại quầy)</h4>
              <AdvanceBookingDaysFields
                allowNegativeMin
                value={formData.staffAdvanceBookingDays}
                onChange={(staffAdvanceBookingDays) =>
                  setFormData((prev) => ({ ...prev, staffAdvanceBookingDays }))
                }
                minLabel="Đặt trước tối thiểu (ngày) cho nhân viên"
                maxLabel="Đặt trước tối đa (ngày) cho nhân viên"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Giới hạn số lần sử dụng/tuần
                  </label>
                  <input
                    type="number"
                    name="usageLimitPerWeek"
                    value={formData.usageLimitPerWeek}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Giới hạn số lần sử dụng/tháng
                  </label>
                  <input
                    type="number"
                    name="usageLimitPerMonth"
                    value={formData.usageLimitPerMonth}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                  />
                </div>
                {selectedUtilityType === 'TIME_BASED' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Giới hạn thời gian sử dụng/tuần (giờ)
                      </label>
                      <input
                        type="number"
                        name="maxUsageTimePerWeek"
                        value={formData.maxUsageTimePerWeek}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Giới hạn thời gian sử dụng/tháng (giờ)
                      </label>
                      <input
                        type="number"
                        name="maxUsageTimePerMonth"
                        value={formData.maxUsageTimePerMonth}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="0"
                      />
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {selectedUtilityType === 'TIME_BASED' ? 'Giới hạn số giờ/đơn đặt' : 'Giới hạn số khung/đơn đặt'}
                  </label>
                  <input
                    type="number"
                    name="maxSlotsPerBooking"
                    value={formData.maxSlotsPerBooking}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tiền đặt cọc (VND)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="depositAmount"
                    value={formatNumberDisplay(formData.depositAmount)}
                    onChange={handleDepositAmountChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Nhập số tiền đặt cọc..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Số tiền cư dân cần thanh toán khi đặt trước. Có thể hoàn lại sau khi sử dụng.</p>
                </div>
              </div>
            </div>
          )}

          {/* Time Slots for FIXED_SLOTS */}
          {selectedUtilityType === 'FIXED_SLOTS' && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Khung giờ mở đặt trước</h3>
                <button
                  type="button"
                  onClick={addTimeSlot}
                  className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm"
                >
                  + Thêm khung giờ
                </button>
              </div>
              {formData.timeSlots.length === 0 && (
                <p className="text-gray-500 text-sm mb-4">Chưa có khung giờ nào. Nhấn "Thêm khung giờ" để bắt đầu.</p>
              )}
              {formData.timeSlots.map((slot, index) => (
                <div key={index} className="border border-gray-200 p-4 rounded-md mb-3 bg-white">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Giờ bắt đầu
                      </label>
                      <input
                        type="time"
                        value={slot.startTime}
                        onChange={(e) => updateTimeSlot(index, 'startTime', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Giờ kết thúc
                      </label>
                      <input
                        type="time"
                        value={slot.endTime}
                        onChange={(e) => updateTimeSlot(index, 'endTime', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sức chứa
                      </label>
                      <input
                        type="number"
                        value={slot.capacity}
                        onChange={(e) => updateTimeSlot(index, 'capacity', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="1"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeTimeSlot(index)}
                    className="mt-2 px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm"
                  >
                    Xóa khung giờ
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Additional Fees */}
          {(selectedUtilityType === 'TIME_BASED' || selectedUtilityType === 'FIXED_SLOTS') && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Thiết lập phụ phí</h3>
                <button
                  type="button"
                  onClick={addAdditionalFee}
                  className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm"
                >
                  + Tạo phụ phí
                </button>
              </div>
              {formData.additionalFees.length === 0 && (
                <p className="text-gray-500 text-sm mb-4">Chưa có phụ phí nào. Nhấn "Tạo phụ phí" để bắt đầu.</p>
              )}
              {formData.additionalFees.map((fee, index) => (
                <div key={index} className="border border-gray-200 p-4 rounded-md mb-3 bg-white">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tên phụ phí
                      </label>
                      <input
                        type="text"
                        value={fee.name}
                        onChange={(e) => updateAdditionalFee(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ví dụ: Phí vệ sinh"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Giá trị (VNĐ)
                      </label>
                      <input
                        type="number"
                        value={fee.amount}
                        onChange={(e) => updateAdditionalFee(index, 'amount', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Thu phí khi
                      </label>
                      <select
                        value={fee.collectWhen}
                        onChange={(e) => updateAdditionalFee(index, 'collectWhen', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="BOOKING">Khi đặt đơn</option>
                        <option value="AFTER_USE">Sau khi sử dụng</option>
                      </select>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAdditionalFee(index)}
                    className="mt-2 px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm"
                  >
                    Xóa phụ phí
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Cancellation Policy */}
          {(selectedUtilityType === 'TIME_BASED' || selectedUtilityType === 'FIXED_SLOTS') && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Thiết lập chính sách hoàn hủy</h3>
                <button
                  type="button"
                  onClick={addCancellationPolicy}
                  className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm"
                >
                  + Tạo chính sách hoàn hủy
                </button>
              </div>
              {formData.cancellationPolicies.length === 0 && (
                <p className="text-gray-500 text-sm mb-4">Chưa có chính sách hoàn hủy nào. Nhấn "Tạo chính sách hoàn hủy" để bắt đầu.</p>
              )}
              {formData.cancellationPolicies.map((policy, index) => (
                <div key={index} className="border border-gray-200 p-4 rounded-md mb-3 bg-white">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Hủy trước (giờ)
                      </label>
                      <input
                        type="number"
                        value={policy.hoursBeforeStart}
                        onChange={(e) => updateCancellationPolicy(index, 'hoursBeforeStart', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="0"
                        placeholder="Ví dụ: 24"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Loại hoàn phí
                      </label>
                      <select
                        value={policy.refundType}
                        onChange={(e) => updateCancellationPolicy(index, 'refundType', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="PERCENTAGE">Phần trăm</option>
                        <option value="FIXED">Cố định</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Giá trị hoàn {policy.refundType === 'PERCENTAGE' ? '(%)' : '(VNĐ)'}
                      </label>
                      <input
                        type="number"
                        value={policy.refundValue}
                        onChange={(e) => updateCancellationPolicy(index, 'refundValue', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="0"
                        max={policy.refundType === 'PERCENTAGE' ? '100' : undefined}
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeCancellationPolicy(index)}
                    className="mt-2 px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm"
                  >
                    Xóa chính sách
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/building-utilities')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Đang xử lý...' : (isEdit ? 'Cập nhật' : 'Tạo mới')}
            </button>
          </div>
        </form>
      </div>

      {/* Pricing Tier Modal */}
      <PricingTierModal
        isOpen={isPricingModalOpen}
        onClose={() => {
          setIsPricingModalOpen(false);
          setEditingServiceHourIndex(null);
        }}
        onSave={(pricingTiers) => {
          if (editingServiceHourIndex !== null) {
            handleServiceHourPricingTiersSave(editingServiceHourIndex, pricingTiers);
          }
          setIsPricingModalOpen(false);
          setEditingServiceHourIndex(null);
        }}
        initialPricingTiers={
          editingServiceHourIndex !== null && formData.serviceHours[editingServiceHourIndex]
            ? (formData.serviceHours[editingServiceHourIndex] as any).pricingTiers || []
            : []
        }
        utilityName={
          editingServiceHourIndex !== null && formData.serviceHours[editingServiceHourIndex]
            ? (() => {
                const hour = formData.serviceHours[editingServiceHourIndex];
                const dayNames = ['Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7', 'Chủ nhật'];
                const days = hour.days.map((d: number) => dayNames[d - 1]).join(', ');
                return `${formData.name} - ${days} (${hour.startTime} - ${hour.endTime})`;
              })()
            : formData.name
        }
        utilityType={selectedUtilityType as 'FIXED_SLOTS' | 'TIME_BASED'}
      />
    </div>
  );
}; 